import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

import { CUSTOM_PRICE_TYPES, CUSTOM_WEIGHT_UNITS, DEFAULT_CURRENCY, SUPPORTED_CURRENCIES } from '../constants/appConstants';
import { formatCurrency } from '../utils/currency';
import { mapCompanyToForm } from '../utils/settingsForm';
import {
  isPricingFormValid,
  buildManualCustomPayload,
  buildCartCustomItem,
  previewWeightedTotal,
  weightUnitShort,
  customItemPriceLabel,
} from '../utils/customItemPricing';

/**
 * Dialog for adding a manually-entered custom OrderItem to a client or cart.
 */
export default function AddCustomItemDialog({
  open,
  onClose,
  clientId,
  onSuccess,
  onAddToCart,
  fullScreen = false,
  title = 'Add Custom Item',
  submitLabel = 'Add',
}) {
  const { company } = useAuth();
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [priceType, setPriceType] = useState(CUSTOM_PRICE_TYPES.FIXED);
  const [weightUnit, setWeightUnit] = useState(CUSTOM_WEIGHT_UNITS.CARAT);
  const [totalPrice, setTotalPrice] = useState('');
  const [weight, setWeight] = useState('');
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;

    setDescription('');
    setPriceType(CUSTOM_PRICE_TYPES.FIXED);
    setWeightUnit(CUSTOM_WEIGHT_UNITS.CARAT);
    setTotalPrice('');
    setWeight('');
    setPricePerUnit('');
    setError('');
    setCurrency(DEFAULT_CURRENCY);

    const companyId = company?.companyId;
    if (!companyId) return;

    axios.get(`/api/companies/${companyId}`)
      .then((res) => {
        const mapped = mapCompanyToForm(res.data);
        if (mapped.defaultItemCurrency) {
          setCurrency(mapped.defaultItemCurrency);
        }
      })
      .catch(() => { /* keep DEFAULT_CURRENCY */ });
  }, [open, company?.companyId]);

  const currencyMeta = SUPPORTED_CURRENCIES.find((c) => c.code === currency);
  const currencySymbol = currencyMeta?.symbol || currency;

  const canSubmit =
    !loading &&
    description.trim() &&
    currency &&
    isPricingFormValid({ priceType, weightUnit, weight, pricePerUnit, totalPrice }) &&
    (onAddToCart || clientId);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      if (onAddToCart) {
        onAddToCart(buildCartCustomItem({
          description,
          currency,
          priceType,
          weightUnit,
          weight,
          pricePerUnit,
          totalPrice,
        }));
        onClose?.();
        return;
      }

      const payload = buildManualCustomPayload({
        clientId,
        description,
        currency,
        priceType,
        weightUnit,
        weight,
        pricePerUnit,
        totalPrice,
      });
      const res = await axios.post('/api/order-items/manual-custom', payload);
      onSuccess?.(res.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const weightedPreview = previewWeightedTotal(weight, pricePerUnit);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth fullScreen={fullScreen}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            autoFocus
            required
            disabled={loading}
            placeholder="e.g. Grading fee, Shipping, Service charge"
          />

          <TextField
            select
            label="Currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            fullWidth
            required
            disabled={loading}
            size="small"
          >
            {SUPPORTED_CURRENCIES.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.code} — {c.name}
              </MenuItem>
            ))}
          </TextField>

          <ToggleButtonGroup
            value={priceType}
            exclusive
            size="small"
            fullWidth
            disabled={loading}
            onChange={(_e, next) => { if (next) setPriceType(next); }}
          >
            <ToggleButton value={CUSTOM_PRICE_TYPES.FIXED}>Total Price</ToggleButton>
            <ToggleButton value={CUSTOM_PRICE_TYPES.BY_WEIGHT}>By Weight</ToggleButton>
          </ToggleButtonGroup>

          {priceType === CUSTOM_PRICE_TYPES.FIXED && (
            <TextField
              label={customItemPriceLabel('total', currency, currencySymbol)}
              type="number"
              inputProps={{ min: 0.01, step: 0.01 }}
              value={totalPrice}
              onChange={(e) => setTotalPrice(e.target.value)}
              fullWidth
              required
              disabled={loading}
            />
          )}

          {priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT && (
            <>
              <ToggleButtonGroup
                value={weightUnit}
                exclusive
                size="small"
                fullWidth
                disabled={loading}
                onChange={(_e, next) => { if (next) setWeightUnit(next); }}
              >
                <ToggleButton value={CUSTOM_WEIGHT_UNITS.CARAT}>Carat (ct)</ToggleButton>
                <ToggleButton value={CUSTOM_WEIGHT_UNITS.GRAM}>Gram (g)</ToggleButton>
              </ToggleButtonGroup>

              <Stack direction="row" spacing={1}>
                <TextField
                  label={`Weight (${weightUnitShort(weightUnit)})`}
                  type="number"
                  inputProps={{ min: 0.0001, step: 0.01 }}
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  fullWidth
                  required
                  disabled={loading}
                />
                <TextField
                  label={`${customItemPriceLabel('perUnit', currency, currencySymbol)}/${weightUnitShort(weightUnit)})`}
                  type="number"
                  inputProps={{ min: 0.01, step: 0.01 }}
                  value={pricePerUnit}
                  onChange={(e) => setPricePerUnit(e.target.value)}
                  fullWidth
                  required
                  disabled={loading}
                />
              </Stack>

              {weightedPreview != null && (
                <Typography variant="body2" color="text.secondary">
                  Total: <strong>{formatCurrency(weightedPreview, currency)}</strong>
                </Typography>
              )}
            </>
          )}

          {error && (
            <Typography variant="caption" color="error">{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
          {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
